import useBackHandler from '../hooks/useBackHandler';
import { sheetSpacing } from '../design/tokens';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Fade } from '@mui/material';
import ScrollDownIndicator from './ScrollDownIndicator';

const EXIT_DURATION_MS = 250;

export default function BottomSheet({
    open, onClose: close, onBack, dismissDisabled = false, children, footer, ariaLabelledBy,
    maxWidth = sheetSpacing.maxWidth,
    px = sheetSpacing.horizontal, pb = sheetSpacing.bottom,
    borderRadius = '20px 20px 0 0',
    handleColor = '#ccc',
    flat = false,
    maxHeight,  // override (e.g. '95vh'). default = vvHeight 기반 동적 처리.
    scrollToBottomKey,
}) {
    const onClose = (...args) => { if (!dismissDisabled) close?.(...args); };
    useBackHandler(open, () => { if (!dismissDisabled) (onBack || close)?.(); });
    const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
    const isNativeIOSApp = typeof window !== 'undefined' && window.IS_NATIVE_APP && window.NATIVE_PLATFORM === 'ios';
    const [visible, setVisible] = useState(false);
    const [kbHeight, setKbHeight] = useState(0);
    const [vvHeight, setVvHeight] = useState(
        () => (typeof window !== 'undefined' && window.visualViewport?.height) || (typeof window !== 'undefined' ? window.innerHeight : 0)
    );
    const [liftForKeyboard, setLiftForKeyboard] = useState(false);
    const [dragY, setDragY] = useState(0);
    const [dragging, setDragging] = useState(false);
    const dragStartY = useRef(null);
    const dragActivated = useRef(false);   // 임계값을 넘어 실제 드래그가 시작됐는지
    const fromHandle = useRef(false);      // 핸들에서 시작했는지 (짧은 탭=닫기 처리용)
    const startScrollTop = useRef(0);
    const scrollRef = useRef(null);
    const focusedFieldRef = useRef(null);
    // Keep the last open frame while the panel slides out. Parent state may already
    // reflect a successful save/login, but that screen belongs to the next opening.
    const presentation = {
        children, footer, maxWidth, px, pb, borderRadius, handleColor, flat, ariaLabelledBy,
        bottom: isNativeIOSApp ? (liftForKeyboard ? kbHeight : 0) : kbHeight,
        maxHeight: maxHeight ?? (isAndroid || (isNativeIOSApp && !liftForKeyboard) ? '85vh' : `${Math.max(vvHeight * 0.85, 200)}px`),
    };
    const lastOpenPresentation = useRef(presentation);
    if (open) lastOpenPresentation.current = presentation;
    const displayed = open ? presentation : lastOpenPresentation.current;

    useEffect(() => {
        if (open) {
            setVisible(true);
        } else {
            setDragY(0);
            setLiftForKeyboard(false);
            setDragging(false);
            dragStartY.current = null;
            dragActivated.current = false;
            fromHandle.current = false;
            const timer = setTimeout(() => setVisible(false), EXIT_DURATION_MS);
            return () => clearTimeout(timer);
        }

    }, [open]);

    useEffect(() => {
        if (!open || !scrollToBottomKey) return;
        window.requestAnimationFrame(() => {
            const scroller = scrollRef.current;
            if (!scroller) return;
            scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
        });
    }, [open, scrollToBottomKey]);

    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;
        // Android는 windowSoftInputMode=adjustResize가 WebView 자체를 키보드 위로 줄여주므로
        // 시트 bottom shift(kbHeight) 적용 시 이중 시프트 → 점프 발생. 시트 bottom은 그대로 둔다.
        // iOS는 RN KeyboardAvoidingView를 쓰지 않고 visualViewport 하나만 기준으로 시트를 올린다.
        if (isAndroid) return;
        const update = () => {
            const visualBottom = (vv.height || window.innerHeight) + (vv.offsetTop || 0);
            setVvHeight(visualBottom);
            setKbHeight(Math.max(window.innerHeight - visualBottom, 0));
        };
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
        };
    }, [isAndroid, isNativeIOSApp]);

    useEffect(() => {
        if (!open) return undefined;
        if (typeof window === 'undefined' || window.NATIVE_PLATFORM !== 'ios') return undefined;

        const shouldLiftWithKeyboard = (target) => target?.dataset?.keyboardLiftAction === 'true';

        const ensureFocusedFieldVisible = (target) => {
            const scroller = scrollRef.current;
            if (!scroller || !target || !scroller.contains(target)) return;
            if (!shouldLiftWithKeyboard(target)) return;

            const scrollerRect = scroller.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const actionRect = Array.from(scroller.querySelectorAll('button'))
                .map((button) => button.getBoundingClientRect())
                .find((rect) => rect.top >= targetRect.bottom - 4);
            const bottomRect = actionRect || targetRect;
            const marginTop = 16;
            const marginBottom = 16;
            const overflowBottom = bottomRect.bottom - (scrollerRect.bottom - marginBottom);
            const overflowTop = (scrollerRect.top + marginTop) - targetRect.top;

            if (overflowBottom > 0) {
                scroller.scrollTo({
                    top: scroller.scrollTop + overflowBottom,
                    behavior: 'auto',
                });
            } else if (overflowTop > 0) {
                scroller.scrollTo({
                    top: Math.max(0, scroller.scrollTop - overflowTop),
                    behavior: 'auto',
                });
            }
        };

        const focusWithoutWebViewScroll = (event) => {
            if (!window.IS_NATIVE_APP) return;
            const target = event.target;
            const tag = target?.tagName?.toLowerCase();
            if (tag !== 'input' && tag !== 'textarea') return;
            // Native pickers must receive the original gesture, without forced focus.
            if (tag === 'input' && ['file', 'date', 'time', 'datetime-local', 'month', 'week'].includes(target.type)) return;
            if (!scrollRef.current?.contains(target)) return;
            if (document.activeElement === target) return;

            const lift = shouldLiftWithKeyboard(target);
            focusedFieldRef.current = lift ? target : null;
            setLiftForKeyboard(lift);
            event.preventDefault();
            target.focus({ preventScroll: true });
        };

        const handleFocusIn = (event) => {
            const target = event.target;
            const tag = target?.tagName?.toLowerCase();
            if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;

            const lift = shouldLiftWithKeyboard(target);
            focusedFieldRef.current = lift ? target : null;
            setLiftForKeyboard(lift);
            if (!lift) return;
            window.setTimeout(() => ensureFocusedFieldVisible(target), 80);
            window.setTimeout(() => ensureFocusedFieldVisible(target), 220);
        };

        const handleViewportChange = () => {
            if (!focusedFieldRef.current) return;
            window.requestAnimationFrame(() => ensureFocusedFieldVisible(focusedFieldRef.current));
        };

        document.addEventListener('touchstart', focusWithoutWebViewScroll, { capture: true, passive: false });
        document.addEventListener('focusin', handleFocusIn);
        window.visualViewport?.addEventListener('resize', handleViewportChange);
        window.visualViewport?.addEventListener('scroll', handleViewportChange);
        return () => {
            document.removeEventListener('touchstart', focusWithoutWebViewScroll, { capture: true });
            document.removeEventListener('focusin', handleFocusIn);
            window.visualViewport?.removeEventListener('resize', handleViewportChange);
            window.visualViewport?.removeEventListener('scroll', handleViewportChange);
            focusedFieldRef.current = null;
            setLiftForKeyboard(false);
        };
    }, [open]);

    // 시트 어디서든 드래그로 닫기 — 본문 스크롤·입력·버튼과 충돌 없이 동작.
    // 정책:
    // 1) input/textarea/select 시작 = 드래그 무시 (키보드/선택 정상 동작)
    // 2) 핸들에서 시작 = 즉시 드래그 활성 (짧은 탭은 닫기로 처리)
    // 3) 본문에서 시작 = 스크롤이 최상단(scrollTop<=0)이고 5px 이상 아래로 끌어야 활성
    //    (스크롤 가능 위치거나 위로 끌면 절대 활성 X — 일반 스크롤로 동작)
    const handleSheetTouchStart = (e) => {
        const target = e.target;
        const tag = target.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select'
            || target.closest?.('button, a, label, [role="button"], [role="slider"], [role="switch"], [data-tappable="true"]')) {
            dragStartY.current = null;
            return;
        }
        const isHandle = !!target.closest?.('[data-sheet-handle="true"]');
        dragStartY.current = e.touches[0].clientY;
        startScrollTop.current = scrollRef.current?.scrollTop || 0;
        fromHandle.current = isHandle;
        dragActivated.current = isHandle;
        if (isHandle) setDragging(true);
    };
    const handleSheetTouchMove = (e) => {
        if (dragStartY.current == null) return;
        const dy = e.touches[0].clientY - dragStartY.current;
        if (!dragActivated.current) {
            // 위로 끌거나 스크롤 가능 위치면 드래그 안 함 — 추적 종료
            if (dy < -3 || startScrollTop.current > 0) {
                dragStartY.current = null;
                return;
            }
            // 아래로 5px 이상 + 스크롤 최상단이면 드래그 활성화
            if (dy > 5) {
                dragActivated.current = true;
                setDragging(true);
                // 시트가 점프하지 않도록 시작점을 현재 위치로 보정
                dragStartY.current = e.touches[0].clientY;
                setDragY(0);
            }
            return;
        }
        const cur = e.touches[0].clientY - dragStartY.current;
        setDragY(Math.max(0, cur));
    };
    const handleSheetTouchEnd = () => {
        if (dragStartY.current == null && !dragActivated.current) return;
        const wasDragging = dragActivated.current;
        const wasHandle = fromHandle.current;
        const distance = dragY;
        dragStartY.current = null;
        dragActivated.current = false;
        fromHandle.current = false;
        setDragging(false);
        if (!wasDragging) return; // 일반 탭/스크롤로 끝남
        // 핸들에서 시작한 짧은 탭은 닫기 (기존 동작 유지)
        if (wasHandle && distance < 5) { onClose(); return; }
        if (distance > 100) { onClose(); }
        else { setDragY(0); }
    };

    // 데스크톱 마우스 드래그는 핸들에서만 (본문 마우스 드래그는 텍스트 선택 등과 충돌)
    const handleHandleMouseDown = (clientY) => {
        dragStartY.current = clientY;
        dragActivated.current = true;
        fromHandle.current = true;
        setDragging(true);
    };
    const handleHandleMouseMove = (clientY) => {
        if (dragStartY.current == null) return;
        const dy = clientY - dragStartY.current;
        setDragY(Math.max(0, dy));
    };
    const handleHandleMouseEnd = () => {
        if (dragStartY.current == null) return;
        const distance = dragY;
        dragStartY.current = null;
        dragActivated.current = false;
        fromHandle.current = false;
        setDragging(false);
        if (distance < 5 || distance > 100) { onClose(); }
        else { setDragY(0); }
    };

    if (!visible) return null;

    return (
        <>
            <Fade in={open} timeout={200}>
                <Box onClick={onClose} data-tappable="true" sx={{
                    position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1200,
                    pointerEvents: open ? 'auto' : 'none',
                }} />
            </Fade>
            <Box
                role={displayed.ariaLabelledBy ? 'dialog' : undefined}
                aria-modal={displayed.ariaLabelledBy ? true : undefined}
                aria-labelledby={displayed.ariaLabelledBy}
                aria-hidden={displayed.ariaLabelledBy ? !open : undefined}
                onTouchStart={handleSheetTouchStart}
                onTouchMove={handleSheetTouchMove}
                onTouchEnd={handleSheetTouchEnd}
                onTouchCancel={() => {
                    dragStartY.current = null;
                    dragActivated.current = false;
                    fromHandle.current = false;
                    setDragging(false);
                    setDragY(0);
                }}
                sx={{
                    position: 'fixed',
                    pointerEvents: open ? 'auto' : 'none',
                    bottom: displayed.bottom,
                    left: '50%',
                    transform: open
                        ? `translateX(-50%) translateY(${dragY}px)`
                        : 'translateX(-50%) translateY(100%)',
                    width: '100%', maxWidth: displayed.maxWidth, zIndex: 1201,
                    bgcolor: '#fff', borderRadius: displayed.borderRadius,
                    // iOS는 키보드 올라오면 visualViewport.height가 줄어드는데 maxHeight가 layout viewport 기준이면
                    // 시트가 viewport top 밖으로 흘러넘쳐 input이 화면 상단 위까지 올라가는 문제 발생.
                    // → vvHeight 기반으로 동적 계산해 키보드 영역 제외한 가용 공간 안에 시트가 들어오도록 함.
                    // 안드로이드는 85vh 유지 — edge-to-edge에서 adjustResize 미동작 시 vvHeight가
                    // 키보드 위 영역으로 줄어 시트가 작아져 input이 키보드에 가리는 회귀 발생.
                    maxHeight: displayed.maxHeight,
                    display: 'flex', flexDirection: 'column',
                    transition: dragging ? 'none' : 'transform 0.25s ease, bottom 0.15s ease',
                    boxShadow: displayed.flat ? 'none' : '0 -4px 20px rgba(0,0,0,0.1)',
                }}>
                <Box
                    data-sheet-handle="true"
                    sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1.5, cursor: 'grab', flexShrink: 0, touchAction: 'none' }}
                    onMouseDown={(e) => handleHandleMouseDown(e.clientY)}
                    onMouseMove={(e) => { if (dragging) handleHandleMouseMove(e.clientY); }}
                    onMouseUp={handleHandleMouseEnd}
                    onMouseLeave={() => { if (dragging) handleHandleMouseEnd(); }}
                >
                    <Box sx={{ width: 40, height: 5, borderRadius: 2.5, bgcolor: displayed.handleColor }} />
                </Box>
                <Box ref={scrollRef} sx={{
                    px: displayed.px, pb: displayed.footer ? 0 : displayed.pb,
                    ...(displayed.footer ? { '& > :last-child': { mb: 0 } } : {}),
                    overflow: dragging ? 'hidden' : 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollPaddingBottom: '32px',
                    overscrollBehavior: 'contain',
                    minHeight: 0,
                    ...(isNativeIOSApp ? {
                        '& .MuiInputBase-input': { fontSize: '16px' },
                        '& textarea': { fontSize: '16px' },
                    } : {}),
                }}>
                    {displayed.children}
                </Box>
                {displayed.footer && <Box data-sheet-footer="true" sx={{
                    flexShrink: 0, bgcolor: '#fff', position: 'relative', zIndex: 1,
                    px: displayed.px, pt: sheetSpacing.action, pb: displayed.pb,
                }}>{displayed.footer}</Box>}
                {!displayed.footer && <ScrollDownIndicator containerRef={scrollRef} position="absolute" bottom={20} center />}
            </Box>
        </>
    );
}
